import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import debounce from "lodash/debounce";

import { useRequest } from "./useRequest";
import routes from "../routes";

export const STAFF_VIEW_STATE_PUT_DEBOUNCE_MS = 400;

/**
 * Internal lifecycle state of the sync hook. The state is not exposed to
 * callers — it exists to make races (e.g. PUTting stale in-memory state
 * before the GET resolves) structurally impossible.
 *
 * - `unauthenticated` — no auth yet, GET hasn't fired, `sync()` is a no-op.
 * - `hydrating` — GET is in flight. `sync()` is still a no-op so callers
 *   can't accidentally write half-initialised state to the server.
 * - `operating` — GET resolved (success, 404, version mismatch, or error
 *   alike). `onHydrate` and/or `onSettled` have fired, and `sync()` now
 *   schedules debounced PUTs.
 */
type State = "unauthenticated" | "hydrating" | "operating";

export interface UseStaffViewStateSyncParams<TState> {
  name: string;
  version: number;
  onHydrate: (state: TState) => void;
  onSettled?: (info: { hydrated: boolean }) => void;
}

export interface UseStaffViewStateSyncResult<TState> {
  sync: (state: TState) => void;
  /**
   * `true` whenever the hook is _not_ actively fetching saved state.
   * Specifically: `false` from the very first render for authenticated
   * sessions (a GET is about to fire), `false` while the GET is in
   * flight, and `true` once the GET has resolved (success, 404, error,
   * or version mismatch). For unauthenticated sessions, where the GET
   * never fires, it stays `true` throughout. Consumers can fold this
   * into a UI loading state to avoid a flash of default values before
   * saved view state has had a chance to hydrate.
   */
  hasSettled: boolean;
}

type ServerPayload = { version?: unknown; state?: unknown };

/**
 * Syncs an arbitrary view-state blob for the current
 * staff user against the `/v3/staff/view-states/:name` endpoint, so things
 * like filters, sort, column visibility, and column order can survive across
 * devices.
 *
 * ### Lifecycle
 *
 * The hook is modelled as a small state machine:
 *
 * ```
 * unauthenticated ──auth_ready──▶ hydrating ──get_resolved──▶ operating
 * ```
 *
 * Side effects per transition:
 * - `unauthenticated → hydrating`: fire `GET /staff/view-states/:name`.
 * - `hydrating → operating`: invoke `onHydrate(state)` (only on version
 *   match), then `onSettled({ hydrated })` (always, with `hydrated` true
 *   when `onHydrate` ran).
 *
 * `sync()` is a no-op outside `operating`. This is deliberate: it forces
 * callers that want a "URL wins, push to server" PUT on first load to do
 * it from `onSettled`, by which point any in-memory state populated from
 * the server (`columnOrder`, etc.) is in sync — so the PUT can't clobber
 * server-only fields with their pre-hydration default.
 *
 * Subsequent `sync(state)` calls schedule a debounced PUT (400ms, see
 * `STAFF_VIEW_STATE_PUT_DEBOUNCE_MS`). A pending PUT is sent straight
 * away on unmount.
 *
 * The hook is keyed by `name`, so different table pages (prospects,
 * clients, solutions, ...) can call it independently and never collide
 * on the server. Changing `name` mid-life resets the state machine back
 * to `unauthenticated` and re-runs the hydration cycle for the new key.
 *
 * @typeParam TState - Shape of the view-state payload owned by the caller.
 *
 * @param params.name - View-state slug. Forms the URL path segment and is
 *   the per-user, per-page key the backend stores against.
 * @param params.version - Integer version of the payload schema. Bump this
 *   whenever `TState` changes incompatibly so old saved blobs are dropped
 *   on hydrate instead of being fed into newer code.
 * @param params.onHydrate - Called once per mount with the saved `state`
 *   when the server returns a version match. Not called on 404, network
 *   error, or version mismatch.
 * @param params.onSettled - Called once per mount immediately after the
 *   `hydrating → operating` transition, regardless of whether `onHydrate`
 *   ran. Use this for "post-hydration" decisions such as pushing the URL
 *   state to the server when the URL had params at mount.
 *
 * @returns An object with:
 * - `sync(state)`: schedules a debounced `PUT` of the latest state. No-op
 *   until the hook reaches the `operating` state.
 */
export function useStaffViewStateSync<TState>({
  name,
  version,
  onHydrate,
  onSettled,
}: UseStaffViewStateSyncParams<TState>): UseStaffViewStateSyncResult<TState> {
  const { isAuthenticated } = useAuth0();
  const { fetch: fetchGet, response, error } = useRequest();
  const { fetch: fetchPut } = useRequest();

  // Refs that mirror identity-volatile values so the hydration effect's
  // dep list can stay focused on `isAuthenticated` + `name`, and so
  // `debouncedPut` can stay stable across Auth0 churn (rebuilding it
  // would let the cleanup below `.flush()` the pending timer on every
  // re-render, defeating the debounce). Written from a
  // dependency-less effect so the writes land after commit rather than
  // during render.
  const onHydrateRef = useRef(onHydrate);
  const onSettledRef = useRef(onSettled);
  const versionRef = useRef(version);
  const fetchGetRef = useRef(fetchGet);
  const fetchPutRef = useRef(fetchPut);
  useEffect(() => {
    onHydrateRef.current = onHydrate;
    onSettledRef.current = onSettled;
    versionRef.current = version;
    fetchGetRef.current = fetchGet;
    fetchPutRef.current = fetchPut;
  });

  const stateRef = useRef<State>("unauthenticated");
  // Starts `false` for authenticated mounts so consumers see "fetch
  // pending" from render 1 — without this, render 1 would expose the
  // initial default `true` for one frame before the auth effect flips
  // it back to `false`, leaking pre-hydration UI to the user. Stays
  // `true` for unauthenticated sessions, where the GET never fires and
  // there's nothing to wait for (also keeps hook tests that don't wrap
  // with Auth0 readable as "no view-state fetch is happening").
  const [hasSettled, setHasSettled] = useState(() => !isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    stateRef.current = "hydrating";
    // setState in effect is required here: a side effect of the auth
    // transition is that consumers see "fetch pending" before any
    // response arrives.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasSettled(false);
    fetchGetRef.current("GET", routes.api.staffViewStates.one({ name }));
    return () => {
      // Reset on auth flip-off or `name` change so the next run starts
      // a fresh hydration cycle. Note: if a previous `name`'s GET is
      // still in flight when `name` changes, its response will land in
      // `useRequest`'s shared response state and the response-handling
      // effect below will process it — this is acceptable for the
      // current single-name-per-instance usage, but a future caller
      // that swaps `name` mid-flight would want request-ID tracking
      // here to discard stale responses.
      stateRef.current = "unauthenticated";
    };
  }, [isAuthenticated, name]);

  useEffect(() => {
    if (stateRef.current !== "hydrating") return;
    if (!response && !error) return;

    const payload = response?.data as ServerPayload | undefined;
    const hydrated = payload?.version === versionRef.current;
    stateRef.current = "operating";
    if (hydrated) {
      onHydrateRef.current(payload.state as TState);
    }
    onSettledRef.current?.({ hydrated });
    // setState in effect is required here: this is the transition out
    // of "hydrating" based on async response arrival.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasSettled(true);
  }, [response, error]);

  const debouncedPut = useMemo(
    () =>
      debounce(
        // The debounced function only runs after a timer, never during
        // render — passing refs is safe here.
        // eslint-disable-next-line react-hooks/refs
        (state: TState) => {
          const url = routes.api.staffViewStates.one({ name });
          fetchPutRef.current("PUT", url, {
            version: versionRef.current,
            state,
          });
        },
        STAFF_VIEW_STATE_PUT_DEBOUNCE_MS,
      ),
    [name],
  );

  useEffect(() => {
    return () => {
      debouncedPut.flush();
    };
  }, [debouncedPut]);

  const sync = useCallback(
    (state: TState) => {
      if (stateRef.current !== "operating") return;
      debouncedPut(state);
    },
    [debouncedPut],
  );

  return { sync, hasSettled };
}
